import React, { useEffect, useState } from 'react';
import { Card, Form, Button, Spinner, Alert } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { FiUpload } from 'react-icons/fi';
import { uploadAndParseBatch, clearParsedData } from './billParsingSlice';
import { LoadingState } from '../../utilities/constants';
import { parseUserInfo } from '../../utilities/common';
import { config } from '../../utilities/config';
import ParsedDataView from './ParsedDataView';

const isDevRealm = config.KEYCLOAK_REALM?.includes('-DEV');

const UploadBillTab = ({ onSuccess }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { orgId } = parseUserInfo(useSelector((state) => state.users.user));
  const { batchParseStatus, batchParseResults } = useSelector((state) => state.billParsing);
  const [files, setFiles] = useState([]);
  const [fileInputKey, setFileInputKey] = useState(Date.now());
  const [isApiV2, setIsApiV2] = useState(false);

  const schema = yup.object().shape({
    selectedFile: yup.mixed().required(t('Please select at least one PDF file'))
  });

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isValid }
  } = useForm({
    resolver: yupResolver(schema),
    mode: 'onChange'
  });

  useEffect(() => {
    if (!batchParseResults) {
      reset();
      setFiles([]);
      setFileInputKey(Date.now());
    }
  }, [batchParseResults, reset]);

  const isParsing = batchParseStatus === LoadingState.pending;
  const isLoading = isParsing;
  const hasResults = batchParseResults;

  const onSubmit = async () => {
    await dispatch(uploadAndParseBatch({ orgId, files, isApiV2 }));
  };

  const handleCancel = () => {
    dispatch(clearParsedData());
    reset();
    setFiles([]);
    setFileInputKey(Date.now());
    setIsApiV2(false);
  };

  return (
    <div>
      <Card className="mb-3">
        <Card.Body>
          <h5 className="mb-3">{t('Upload PDF Bill')}</h5>

          <Form onSubmit={handleSubmit(onSubmit)}>
            <Form.Group>
              <Controller
                name="selectedFile"
                control={control}
                render={({ field }) => {
                  const handleSelectFile = (e) => {
                    const fileList = Array.from(e.target.files);
                    if (fileList.length > 0) {
                      field.onChange(fileList[0]);
                      setFiles(fileList);
                    }
                  };
                  return (
                    <>
                      <Form.File
                        key={fileInputKey}
                        id="billFileUploadControl"
                        label={t('Select PDF File')}
                        accept=".pdf,application/pdf"
                        className="form-content"
                        onChange={handleSelectFile}
                        isInvalid={!!errors.selectedFile}
                        disabled={isLoading || hasResults}
                        multiple
                      />
                      {errors.selectedFile && <span className="required-span">{errors.selectedFile.message}</span>}
                    </>
                  );
                }}
              />
            </Form.Group>

            {isDevRealm && (
              <Form.Group>
                <Form.Check
                  type="checkbox"
                  id="isApiV2Checkbox"
                  label={t('Use API v2')}
                  checked={isApiV2}
                  onChange={(e) => setIsApiV2(e.target.checked)}
                  disabled={isLoading || hasResults}
                />
              </Form.Group>
            )}

            {isLoading && (
              <Alert variant="info" className="d-flex align-items-center">
                <Spinner animation="border" size="sm" className="mr-2" />
                <span>{t('Processing...')}</span>
              </Alert>
            )}

            <Button
              type="submit"
              variant="secondary"
              disabled={!isValid || isLoading || hasResults}
              className="d-flex align-items-center"
            >
              <FiUpload className="mr-2" />
              {t('Upload & Parse')}
            </Button>
          </Form>
        </Card.Body>
      </Card>

      {batchParseResults && (
        <ParsedDataView parsedData={batchParseResults} onCancel={handleCancel} onSuccess={onSuccess} />
      )}
    </div>
  );
};

export default UploadBillTab;
